import { adminCasesQuery } from "@/api/queries";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { caseStatusConfig } from "@/lib/adminLabels";
import type { CaseStatus } from "@/types/admin";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { AlertTriangle, Loader2, Search } from "lucide-react";
import { useState } from "react";

export const Route = createFileRoute("/admin/")({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(adminCasesQuery({})),
  component: AdminCasesPage,
});

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: "", label: "Alla" },
  { value: "active", label: "Aktiva" },
  { value: "monitoring", label: "Övervakning" },
  { value: "completed", label: "Slutförda" },
  { value: "cancelled", label: "Avbrutna" },
];

function AdminCasesPage() {
  const navigate = Route.useNavigate();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");

  const { data: cases, isError } = useQuery({
    ...adminCasesQuery({ status: status || undefined, q: q || undefined }),
    placeholderData: keepPreviousData,
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-xs">
          <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Sök namn eller e-post…"
            className="pl-8"
          />
        </div>
        <div className="flex flex-wrap gap-1">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setStatus(f.value)}
              className={
                "rounded-md px-3 py-1.5 text-sm font-medium transition-colors " +
                (status === f.value
                  ? "bg-muted text-foreground"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground")
              }
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <Card className="overflow-hidden p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-muted">
              <TableRow>
                <TableHead>Kund</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Framsteg</TableHead>
                <TableHead>Åtgärd</TableHead>
                <TableHead>Skapad</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isError ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="py-12 text-center text-red-600"
                  >
                    Kunde inte hämta ärenden.
                  </TableCell>
                </TableRow>
              ) : !cases ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-12 text-center">
                    <Loader2 className="mx-auto size-6 animate-spin text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : cases.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="py-12 text-center text-muted-foreground"
                  >
                    Inga ärenden hittades.
                  </TableCell>
                </TableRow>
              ) : (
                cases.map((c) => (
                  <TableRow
                    key={c.id}
                    className="cursor-pointer"
                    onClick={() =>
                      navigate({
                        to: "/admin/cases/$caseId",
                        params: { caseId: c.id },
                      })
                    }
                  >
                    <TableCell>
                      <div className="font-medium">{c.customerName ?? "—"}</div>
                      <div className="text-xs text-muted-foreground">
                        {c.customerEmail}
                      </div>
                    </TableCell>
                    <TableCell>
                      <CaseStatusBadge status={c.status} />
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-sm">
                        {c.counts.confirmed}/{c.counts.total}
                      </span>
                      <span className="ml-1 text-xs text-muted-foreground">
                        bekräftade
                      </span>
                    </TableCell>
                    <TableCell>
                      {c.counts.attention > 0 ? (
                        <Badge className="bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 border-amber-300 dark:border-amber-800">
                          <AlertTriangle className="size-3" />
                          {c.counts.attention}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(c.createdAt).toLocaleDateString("sv-SE")}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}

function CaseStatusBadge({ status }: { status: CaseStatus }) {
  const config = caseStatusConfig[status];
  return <Badge className={config.className}>{config.label}</Badge>;
}
